import apiClient from '@/services/axios'

const PathwayAPI = {
  /**
   * Mengambil semua pathway berdasarkan ID kategori onkologi.
   * @param {number} oncologyId - ID dari OncologyCategory.
   * @returns {Promise<object>} Data pathway.
   */
  getPathwaysByOncology: async oncologyId => {
    const response = await apiClient.get('/treatment-pathways', {
      params: { oncologyId },
    })
    return response.data
  },

  /**
   * Mengambil detail satu pathway berdasarkan ID-nya.
   * @param {number} id - ID dari TreatmentPathway.
   * @returns {Promise<object>} Data detail pathway.
   */
  getPathwayById: async id => {
    const response = await apiClient.get(`/treatment-pathways/${id}`)
    return response.data
  },

  /**
   * Membuat pathway baru.
   * @param {object} pathwayData - Data untuk pathway baru (JSON).
   * @returns {Promise<object>} Data pathway yang baru dibuat.
   */
  createPathway: async pathwayData => {
    const response = await apiClient.post('/treatment-pathways', pathwayData)
    return response.data
  },

  /**
   * Memperbarui pathway yang ada.
   * @param {number} id - ID pathway yang akan diupdate.
   * @param {object} updateData - Data yang akan diubah (JSON).
   * @returns {Promise<object>} Data pathway yang sudah diupdate.
   */
  updatePathway: async (id, updateData) => {
    const response = await apiClient.patch(`/treatment-pathways/${id}`, updateData)
    return response.data
  },

  /**
   * Menghapus pathway (dan semua turunannya).
   * @param {number} id - ID pathway yang akan dihapus.
   * @returns {Promise<object>} Pesan sukses.
   */
  deletePathway: async id => {
    const response = await apiClient.delete(`/treatment-pathways/${id}`)
    return response.data
  },
}

const DrugAPI = {
  /**
   * Mengambil semua data obat.
   * @returns {Promise<Array>} Daftar obat.
   */
  getAllDrugs: async () => {
    const response = await apiClient.get('/drug')
    return response.data
  },

  /**
   * Membuat obat baru (termasuk upload file).
   * PENTING: drugData harus berupa objek FormData.
   * @param {FormData} drugData - Data obat dalam bentuk FormData.
   * @returns {Promise<object>} Data obat yang baru dibuat.
   */
  createDrug: async drugData => {
    const response = await apiClient.post('/drug', drugData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * Memperbarui obat yang ada.
   * PENTING: drugData harus berupa objek FormData jika ada file yang diubah.
   * @param {number} id - ID obat yang akan diupdate.
   * @param {FormData} drugData - Data obat dalam bentuk FormData.
   * @returns {Promise<object>} Data obat yang sudah diupdate.
   */
  updateDrug: async (id, drugData) => {
    // Backend kita menggunakan PUT untuk update drug
    const response = await apiClient.put(`/drug/${id}`, drugData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * Menghapus obat.
   * @param {number} id - ID obat yang akan dihapus.
   * @returns {Promise<object>} Pesan sukses.
   */
  deleteDrug: async id => {
    const response = await apiClient.delete(`/drug/${id}`)
    return response.data
  },
}

const UserManagementAPI = {
  /**
   * Mengambil semua data Admin atau User.
   * @returns {Promise<Array>} Daftar Admin atau User.
   */
  getAllUsers: async params => {
    const response = await apiClient.get(`/users?role=${params}`)
    return response.data
  },

  /**
   * Membuat Admin baru.
   * @returns {Promise<object>} Data Admin yang baru dibuat.
   */
  createAdmin: async adminData => {
    const response = await apiClient.post('/auth/register-admin', adminData)
    return response.data
  },

  /**
   * Memperbarui Admin yang ada.
   * @returns {Promise<object>} Data Admin yang sudah diperbarui.
   */
  updateAdmin: async adminData => {
    const response = await apiClient.put('/auth/admin', adminData)
    return response.data
  },

  /**
   * Menghapus Admin.
   * @param {number} id - ID Admin yang akan dihapus.
   * @returns {Promise<object>} Pesan sukses.
   */
  deleteAdmin: async id => {
    const response = await apiClient.delete(`/auth/admin/${id}`)
    return response.data
  },

  /**
   * Mengambil semua Job Title.
   * @returns {Promise<Array>} Daftar Job Title.
   */
  getAllJobTitles: async () => {
    const response = await apiClient.get('/user/job-titles')
    return response.data
  },

  /**
   * Mengirimkan permintaan untuk mengatur ulang kata sandi.
   * @returns {Promise<object>} Pesan sukses dan atribut exist (true: email memiliki akun admin, false: email tidak memiliki akun admin).
   */
  forgotPassword: async email => {
    const response = await apiClient.post('/auth/forgot-password', { email })
    return response.data
  },

  /**
   * Mengirimkan permintaan untuk mengubah kata sandi.
   * @returns {Promise<object>} Pesan sukses.
   */
  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post(`/auth/reset-password?token=${token}`, { newPassword })
    return response.data
  },
}

export { PathwayAPI, DrugAPI, UserManagementAPI }
